import React, {
    FC,
    PropsWithChildren,
    useCallback,
    useMemo,
    useState,
} from 'react';

import { HotelMediaResponse } from 'proto/Api/Hotel/HotelMedia';
import { Category } from 'proto/Hotel/HotelMedia';
import { UserRating } from 'proto/User/Rating';

import {
    CategoryType,
    GalleryType,
    GuestRatingImage,
    OpenGalleryProps,
} from '../types';

import { GalleryAction, GalleryData } from './types';

export const GalleryContext = React.createContext<GalleryData>({
    galleryType: GalleryType.Images,
    category: Category.All,
});

export const GalleryActionContext = React.createContext<GalleryAction>({
    updateData() {},
    switchType() {},
    switchCategory() {},
    setCurrentGuestRating() {},
    setCurrentGuestRatingImage() {},
    openHotelGallery() {},
});

const GalleryContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const [galleryData, setGalleryData] = useState<HotelMediaResponse>();
    const [galleryType, setGalleryType] = useState<GalleryType>(
        GalleryType.Images
    );
    const [categoryType, setCategoryType] = useState<CategoryType>(
        Category.All
    );
    const [openGalleryParams, setOpenGalleryParams] =
        useState<OpenGalleryProps | undefined>();
    const [currentGuestRating, setCurrentGuestRating] =
        useState<UserRating | undefined>();
    const [currentGuestRatingImage, setCurrentGuestRatingImage] =
        useState<GuestRatingImage | undefined>();

    const contextValue = useMemo<GalleryData>(
        () => ({
            response: galleryData,
            galleryType: galleryType,
            category: categoryType,
            hotelParams: openGalleryParams,
            currentGuestRating: currentGuestRating,
            currentGuestRatingImage: currentGuestRatingImage,
        }),
        [
            galleryData,
            galleryType,
            categoryType,
            openGalleryParams,
            currentGuestRating,
            currentGuestRatingImage,
        ]
    );

    const updateData = useCallback(
        (galleryData: HotelMediaResponse | undefined) => {
            setGalleryData(galleryData);
            if (
                galleryData?.hotelMedia?.imageList.length === 0 &&
                galleryData.hotelMedia.guestImageList.length > 0
            ) {
                setGalleryType(GalleryType.GuestImages);
            } else {
                setGalleryType(GalleryType.Images);
            }
        },
        []
    );

    const actionMap = useMemo<GalleryAction>(
        () => ({
            updateData,
            switchType: setGalleryType,
            switchCategory: setCategoryType,
            openHotelGallery: setOpenGalleryParams,
            setCurrentGuestRating,
            setCurrentGuestRatingImage,
        }),
        [updateData]
    );

    return (
        <GalleryActionContext.Provider value={actionMap}>
            <GalleryContext.Provider value={contextValue}>
                {children}
            </GalleryContext.Provider>
        </GalleryActionContext.Provider>
    );
};

export default GalleryContextProvider;
